import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { OpenAIBaseService } from "./open-ai-base.service";

export interface ParsedJobData {
  title?: string;
  company?: string;
  company_website?: string;
  type?: string;
  work_type?: string;
  location?: string;
  salary_min: number | null;
  salary_max: number | null;
  salaryRange: string;
  jobType: string;
  short_description: string;
  about_company: string;
  responsibilities: string[];
  requirements: string[];
  benefits: string[];
}

/**
 * Service for parsing job descriptions using OpenAI
 */
@Injectable({
  providedIn: "root",
})
export class JobParserService extends OpenAIBaseService {
  constructor(protected override http: HttpClient) {
    super();
  }

  /**
   * Parse a job description using OpenAI
   */
  async parseJobDescription(description: string): Promise<ParsedJobData> {
    const prompt = `Extract the following information from this job description in JSON format:
    - title: the job title
    - company: company name
    - company_website: company website URL (look for URLs, website mentions, or "apply at" links - extract the main company domain, not job board URLs)
    - type: job type (Full-time, Part-time, Contract, or Freelance)
    - work_type: working arrangement (remote, hybrid, or onsite)
    - location: job location (especially for hybrid or onsite jobs)
    - salary_min: minimum salary (number or null if not specified)
    - salary_max: maximum salary (number or null if not specified)
    - salaryRange: salary range
    - jobType: job type
    - short_description: a brief 2-3 sentence summary
    - about_company: detailed information about the company including its mission, values, products/services, company culture, size, history, growth, achievements, work environment, and any other relevant details. Extract as much company information as possible from the job description.
    - responsibilities: array of key responsibilities
    - requirements: array of job requirements
    - benefits: array of benefits offered

    Job Description:
    ${description}`;

    const systemPrompt =
      "You are a helpful assistant that extracts structured data from job descriptions. Return the data as JSON without any markdown formatting.";

    try {
      console.log("Sending request to OpenAI API to parse job description...");

      const content = await this.makeOpenAIChatRequest(
        prompt,
        systemPrompt,
        "gpt-4o-mini",
        0.3
      );

      try {
        const parsedData = this.parseJsonResponse(content);
        console.log("Successfully parsed job data from description");

        // Validate parsed data
        this.validateParsedData(parsedData);

        return parsedData;
      } catch (e) {
        console.error("Parse error for content from OpenAI");
        console.error("Parse error details:", e);
        throw new Error("Failed to parse OpenAI response");
      }
    } catch (error) {
      console.error("Error calling OpenAI API:", error);
      throw error;
    }
  }

  /**
   * Validate the parsed job data
   */
  private validateParsedData(data: any): asserts data is ParsedJobData {
    // Check that all required fields exist
    const requiredFields = [
      "title",
      "company",
      "type",
      "work_type",
      "location",
      "salary_min",
      "salary_max",
      "short_description",
      "about_company",
      "responsibilities",
      "requirements",
      "benefits",
    ];

    // Optional fields don't need validation for existence, but we'll validate their types if present
    const optionalFields = ["salaryRange", "jobType", "company_website"];

    for (const field of requiredFields) {
      if (!(field in data)) {
        throw new Error(`Missing required field '${field}' in parsed data`);
      }
    }

    // Check that arrays are actually arrays
    const arrayFields = ["responsibilities", "requirements", "benefits"];
    for (const field of arrayFields) {
      if (!Array.isArray(data[field])) {
        throw new Error(`Field '${field}' should be an array`);
      }
    }

    // Check that salary fields are numbers or null
    if (data.salary_min !== null && typeof data.salary_min !== "number") {
      throw new Error(`Field 'salary_min' should be a number or null`);
    }

    if (data.salary_max !== null && typeof data.salary_max !== "number") {
      throw new Error(`Field 'salary_max' should be a number or null`);
    }

    // Check that text fields are strings
    const textFields = ["short_description", "about_company"];
    for (const field of textFields) {
      if (typeof data[field] !== "string") {
        throw new Error(`Field '${field}' should be a string`);
      }
    }

    // Check optional text fields if present
    for (const field of optionalFields) {
      if (
        field in data &&
        data[field] !== null &&
        typeof data[field] !== "string"
      ) {
        throw new Error(
          `Optional field '${field}' should be a string when present`
        );
      }
    }
  }
}
